import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Alert, AlertDescription } from '../ui/alert';
import { AlertTriangle, Paperclip, X, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'hours_incorrect', label: 'Hours are incorrect' },
  { value: 'rate_incorrect', label: 'Contract rate is incorrect' },
  { value: 'missing_reimbursement', label: 'Missing reimbursement' },
  { value: 'missing_additional_time', label: 'Missing approved additional time' },
  { value: 'other', label: 'Other' },
];

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

// Contractor-facing Settlement Challenge modal (2026-06 Phase 2).
export default function SettlementChallengeModal({ open, onClose, settlement, onSubmitted }) {
  const [category, setCategory] = useState('');
  const [explanation, setExplanation] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [ack, setAck] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const s = settlement || {};

  const reset = () => { setCategory(''); setExplanation(''); setAttachments([]); setAck(false); };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/messages/attachments/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAttachments((prev) => [...prev, { url: res.data.url, name: res.data.filename, kind: res.data.kind }]);
    } catch (err) {
      toast.error('Could not upload that file. Please try another.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const submit = async () => {
    if (!category) { toast.error('Please choose what you are challenging.'); return; }
    if (!explanation.trim()) { toast.error('Please tell us what should be corrected.'); return; }
    if (!ack) { toast.error('Please acknowledge the review timeline.'); return; }
    setSubmitting(true);
    try {
      await api.post(`/talent/settlements/${s.booking_id}/challenge`, {
        category,
        explanation: explanation.trim(),
        attachments,
        acknowledged: true,
      });
      toast.success('Settlement challenge submitted. It is now under review.');
      reset();
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not submit your challenge. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Could not submit your challenge.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="settlement-challenge-modal">
        <DialogHeader>
          <DialogTitle className="text-base">Challenge Settlement</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Tell us what looks incorrect. We will review and update your settlement.
          </DialogDescription>
        </DialogHeader>

        {/* Recorded settlement snapshot */}
        <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 text-sm">
          <div className="flex justify-between p-2.5"><span className="text-slate-600">Recorded hours</span><span className="font-medium text-slate-900">{s.hours_worked ?? '—'} hrs</span></div>
          <div className="flex justify-between p-2.5"><span className="text-slate-600">Contract rate</span><span className="font-medium text-slate-900">{s.contract_rate != null ? `$${Number(s.contract_rate).toFixed(2)}/hr` : '—'}</span></div>
          <div className="flex justify-between p-2.5"><span className="text-slate-600">Reimbursements</span><span className="text-slate-900">{s.reimbursements ? fmt(s.reimbursements) : '—'}</span></div>
          <div className="flex justify-between p-2.5 bg-slate-50"><span className="text-slate-700 font-semibold">Current settlement amount</span><span className="font-bold text-slate-900">{fmt(s.settlement_amount)}</span></div>
        </div>

        {/* Category */}
        <div>
          <label className="text-sm font-medium text-slate-700">What are you challenging?</label>
          <div className="mt-2 space-y-1.5">
            {CATEGORIES.map((c) => (
              <label key={c.value} className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer text-sm ${category === c.value ? 'border-cyan-500 bg-cyan-50' : 'border-slate-200'}`} data-testid={`challenge-category-${c.value}`}>
                <input type="radio" name="challenge-category" value={c.value} checked={category === c.value} onChange={() => setCategory(c.value)} className="accent-cyan-600" />
                <span className="text-slate-800">{c.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Explanation */}
        <div>
          <label className="text-sm font-medium text-slate-700">Tell us what should be corrected and why</label>
          <Textarea
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            rows={4}
            placeholder="Example: I clocked out at 6:00 PM so my hours should be 8, not 6.5."
            className="mt-1.5"
            data-testid="challenge-explanation"
          />
        </div>

        {/* Attachments */}
        <div>
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-slate-700">Supporting documents (optional)</label>
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="challenge-attach-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Paperclip className="w-4 h-4 mr-1" /> Attach</>}
            </Button>
            <input ref={fileRef} type="file" className="hidden" onChange={handleFile} accept="image/*,application/pdf" />
          </div>
          {attachments.length > 0 && (
            <div className="mt-2 space-y-1">
              {attachments.map((a, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1">
                  <span className="truncate text-slate-700">{a.name}</span>
                  <button type="button" onClick={() => setAttachments((p) => p.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 5 business day acknowledgment */}
        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <AlertDescription className="text-xs text-slate-700">
            <span className="font-medium">Important:</span> Submitting a settlement challenge places this settlement under review and may add an additional 5 business days to the settlement review process.
          </AlertDescription>
        </Alert>
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="challenge-ack" />
          <span className="text-slate-700">I understand this may add up to 5 additional business days to my settlement review.</span>
        </label>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onClose?.()} data-testid="challenge-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="challenge-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Submit Challenge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
